import styled from '@emotion/styled'
import { DateTime } from 'luxon'
import { FunctionComponent, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useGamNetworks } from '~/api/admin/admin-gam-networks.api'
import { useAdminScopes } from '~/api/admin/admin-scope.api'
import { useCreateSnoozeAlarms, useDeleteSnoozeAlarms, useSnoozeAlarms } from '~/api/admin/snooze-alarms.api'
import Button from '~/components/Button'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import Table, { Column, EmptyTable } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'

const Wrapper = styled.div`
  margin: 20px;
`

const NewConfigForm = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`
export type ConfigFormValues = { type: string; resourceId: string }

// Extracted delete button as a component
const DeleteSnoozeAlarmButton = ({ id }: { id: string }) => {
  const { mutateAsync: deleteSnoozeAlarm } = useDeleteSnoozeAlarms(id, { errorToast: 'An error occurred when deleting the snoozed alarm' })
  const handleDelete = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    await deleteSnoozeAlarm()
  }
  return <Button iconName={'delete'} variant={'tertiary'} onClick={handleDelete} trackingLabel={'ana-admin-snooze-alarm-delete'} />
}

export const AdminSnoozeAlarm: FunctionComponent = () => {
  // API hooks
  const { data: snoozeAlarms, isFetching, refetch } = useSnoozeAlarms()
  const { mutateAsync: createSnoozeAlarm } = useCreateSnoozeAlarms({ errorToast: 'An error occurred when creating the snoozed alarm' })
  const { data: scopes } = useAdminScopes()
  const { data: allGamNetwork } = useGamNetworks()
  const gamNetworks = useMemo(() => (allGamNetwork || []).map((t) => ({ id: t.id })), [allGamNetwork])
  // Hooks

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ConfigFormValues>({
    mode: 'onChange',
    defaultValues: { type: '', resourceId: '' },
  })
  const type = useWatch({
    control,
    name: 'type',
  })
  const option: SelectOptionProp[] =
    type === 'gam'
      ? gamNetworks.map((t) => ({ label: t.id, value: t.id, trackingLabel: 'ana-admin-snooze-alarm-gam-network-set' }))
      : scopes.map((t) => ({ label: t.name, value: t.id, trackingLabel: 'ana-admin-snooze-alarm-scope-set' }))
  const onSubmit = async (formValues: ConfigFormValues) => {
    await createSnoozeAlarm({ type: formValues.type, resourceId: formValues.resourceId })
  }

  const COLUMNS: Column[] = [
    {
      name: 'Scope Or Network',
      isSortable: false,
    },
    {
      name: 'Type',
      isSortable: false,
    },
    {
      name: 'Expiration Time',
      isSortable: false,
    },
    {
      name: 'Action',
      isSortable: false,
    },
  ]

  return isFetching ? (
    <Skeleton bigger width={'100%'} />
  ) : (
    <Wrapper>
      <NewConfigForm>
        <p>Snooze an alarm for one week</p>
        <Select
          control={control}
          name={'type'}
          label={'Type'}
          options={[
            { label: 'Gam', value: 'gam', trackingLabel: 'ana-admin-snooze-alarm-gam-set' },
            { label: 'Darty', value: 'darty', trackingLabel: 'ana-admin-snooze-alarm-darty-set' },
          ]}
          rules={{ required: true }}
        />
        <Select searchable={true} disabled={!type} control={control} name={'resourceId'} label={'resourceId'} options={option} rules={{ required: 'Please fill this to continue' }} />
        <Button onClick={handleSubmit(onSubmit)} disabled={!isValid} trackingLabel={'ana-admin-snooze-alarm-save'}>
          Add snooze alarm
        </Button>
      </NewConfigForm>
      <Table isLoading={false} columns={COLUMNS}>
        {snoozeAlarms ? (
          snoozeAlarms.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.resourceId}</TableCell>
              <TableCell>{c.type}</TableCell>
              <TableCell>{c.expirationTime ? DateTime.fromISO(c.expirationTime).toFormat('yyyy-LL-dd HH:mm:ss') : 'never'}</TableCell>
              <TableCell>
                <DeleteSnoozeAlarmButton id={c.id} />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={5}>
              <EmptyTable>
                <div>
                  <h3>Something happened</h3>
                  <p>An error occurred when getting snoozed alarms.</p>
                </div>
                <Button
                  onClick={async () => {
                    await refetch()
                  }}
                  trackingLabel={'ana-admin-snooze-alarm-refresh'}
                >
                  Try again
                </Button>
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </Wrapper>
  )
}
